import { useRef, useCallback, useEffect } from 'react';
import type { ChatChannelResponse, ChatMessageResponse } from '@/lib/api';
import { useAuth } from '@/auth';
import { useChatSettings } from './useChatSettings';

const CHIME_SRC = '/chime.mp3';

interface UseChatNotificationsReturn {
  fireMessageNotification: (
    channelId: string,
    senderId: string,
    senderName: string | null,
    content: string,
    mentions?: Array<{ userId: string; displayName: string }> | null,
  ) => void;
  fireReactionNotification: (messageId: string, emoji: string) => void;
  fireChannelAddedNotification: () => void;
}

export function useChatNotifications(
  channelsRef: React.MutableRefObject<{ channels: ChatChannelResponse[] }>,
  messagesRef: React.MutableRefObject<{ messages: ChatMessageResponse[] }>,
): UseChatNotificationsReturn {
  const { user } = useAuth();
  const { settings } = useChatSettings();

  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const currentUserIdRef = useRef<string | null>(user?.userId ?? null);
  currentUserIdRef.current = user?.userId ?? null;

  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = new Audio(CHIME_SRC);
    audio.preload = 'auto';
    audioRef.current = audio;
    return () => {
      audioRef.current = null;
    };
  }, []);

  // Request browser notification permission once when any notification type is enabled
  useEffect(() => {
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'default') return;
    const { notificationNewMessages, notificationReactions, notificationChannelAdded } =
      settingsRef.current;
    if (notificationNewMessages !== 'never' || notificationReactions || notificationChannelAdded) {
      Notification.requestPermission().catch(() => {});
    }
  }, [
    settings.notificationNewMessages,
    settings.notificationReactions,
    settings.notificationChannelAdded,
  ]);

  const playChime = useCallback((isDM: boolean, force = false) => {
    const s = settingsRef.current;
    // A direct @mention is prominent enough to chime even when the channel's
    // per-type sound is off (mentions pierce mute — matches Slack/Discord).
    if (!force && (isDM ? !s.notificationSoundDMs : !s.notificationSoundChannels)) return;
    if (!audioRef.current) return;
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(() => {});
  }, []);

  const showBrowserNotification = useCallback((title: string, body: string) => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    try {
      new Notification(title, { body, icon: '/favicon.ico' });
    } catch {
      // Notification API unavailable in this context
    }
  }, []);

  const fireMessageNotification = useCallback(
    (
      channelId: string,
      senderId: string,
      senderName: string | null,
      content: string,
      mentions?: Array<{ userId: string; displayName: string }> | null,
    ) => {
      const s = settingsRef.current;
      const me = currentUserIdRef.current;
      if (senderId === me) return;
      // Global "never" silences everything, including mentions (the user has no
      // finer mention-only control by design — see the chat notification settings).
      if (s.notificationNewMessages === 'never') return;

      const isMention = !!mentions?.some((m) => m.userId === me);

      const channel = channelsRef.current.channels.find((c) => c.channelId === channelId);
      // A direct @mention pierces mute and the "DMs only" filter; a normal message
      // in a muted channel stays silent (the badge still counts it server-side).
      if (channel?.isMuted && !isMention) return;
      const isDM = channel?.channelType === 'direct';
      if (s.notificationNewMessages === 'dms' && !isDM && !isMention) return;

      playChime(isDM, isMention);

      const displayName = senderName ?? 'Someone';
      const channelName = channel?.dmPartnerDisplayName ?? channel?.name ?? 'Chat';
      const body =
        content
          .replace(/<[^>]+>/g, '')
          .trim()
          .slice(0, 100) || 'New message';
      // Tagged messages read differently from general chat updates (the tester's
      // "general chat update vs tagged message" ask).
      const title = isMention
        ? isDM
          ? `${displayName} mentioned you`
          : `${displayName} mentioned you in ${channelName}`
        : isDM
          ? displayName
          : `${displayName} in ${channelName}`;
      showBrowserNotification(title, body);
    },
    [channelsRef, playChime, showBrowserNotification],
  );

  const fireReactionNotification = useCallback(
    (messageId: string, emoji: string) => {
      if (!settingsRef.current.notificationReactions) return;
      const message = messagesRef.current.messages.find((m) => m.messageId === messageId);
      if (!message || message.senderId !== currentUserIdRef.current) return;

      showBrowserNotification('New Reaction', `Someone reacted ${emoji} to your message`);
    },
    [messagesRef, showBrowserNotification],
  );

  const fireChannelAddedNotification = useCallback(() => {
    if (!settingsRef.current.notificationChannelAdded) return;
    showBrowserNotification('Added to Channel', 'You were added to a new group or channel');
  }, [showBrowserNotification]);

  return { fireMessageNotification, fireReactionNotification, fireChannelAddedNotification };
}
